import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Inbox,
  Users,
  BarChart3,
  Settings,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  type LucideIcon,
} from "lucide-react";

import { AllisonOrb } from "@/components/alia/allison-orb";
import { Logo } from "@/components/alia/logo";
import { useIsMobile } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  count?: number;
  disabled?: boolean;
}

const NAV: NavItem[] = [
  { to: "/inbox", label: "Inbox", icon: Inbox, count: 6 },
  { to: "/leads", label: "Leads", icon: Users, disabled: true },
  { to: "/analytics", label: "Analítica", icon: BarChart3, disabled: true },
  { to: "/agents", label: "Agentes", icon: Sparkles, disabled: true },
];

const STORAGE_KEY = "alia.sidebar.pinned";

interface SidebarProps {
  /** Estado del drawer mobile - controlado desde AppShell */
  mobileOpen?: boolean;
  /** Callback para cerrar el drawer mobile */
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const isMobile = useIsMobile();

  // Pinned state persists across reloads (solo aplica a desktop)
  const [pinned, setPinned] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(pinned));
    } catch {
      /* noop */
    }
  }, [pinned]);

  // En mobile, el sidebar siempre está "expandido" cuando está visible
  const expanded = isMobile ? true : pinned || hovered;

  // ESC para cerrar mobile drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onMobileClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, onMobileClose]);

  // Body scroll lock cuando el drawer mobile está abierto
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [isMobile, mobileOpen]);

  // Cerrar drawer al hacer tap en un nav link (mobile)
  function handleNavClick() {
    if (isMobile) onMobileClose?.();
  }

  return (
    <>
      {/* Spacer DESKTOP — keeps the rest of the layout from shifting when hovering */}
      <div
        className={cn(
          "hidden shrink-0 transition-[width] duration-200 lg:block",
          pinned ? "w-56" : "w-14"
        )}
        aria-hidden
      />

      {/* Overlay MOBILE - aparece detrás del drawer cuando está abierto */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] transition-opacity duration-300 lg:hidden",
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={onMobileClose}
        aria-hidden
      />

      {/* Sidebar — distintos comportamientos según viewport */}
      <aside
        onMouseEnter={() => !isMobile && setHovered(true)}
        onMouseLeave={() => !isMobile && setHovered(false)}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-screen flex-col border-r border-border bg-surface transition-all duration-300 ease-out",
          // Desktop (lg+): siempre visible, ancho según pinned/hovered
          "lg:z-30",
          isMobile
            ? // Mobile: drawer slide-in
              cn(
                "w-72 max-w-[85vw] shadow-2xl",
                mobileOpen ? "translate-x-0" : "-translate-x-full"
              )
            : // Desktop: ancho variable, siempre presente
              cn(
                "translate-x-0",
                expanded ? "w-56 shadow-2xl" : "w-14"
              )
        )}
      >
        {/* Brand row */}
        <div
          className={cn(
            "flex h-14 items-center px-3",
            expanded ? "justify-between" : "justify-center"
          )}
        >
          <Logo size={22} showWordmark={expanded} />
          {expanded && !isMobile && (
            <button
              onClick={() => setPinned((v) => !v)}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground"
              aria-label={pinned ? "Colapsar sidebar" : "Fijar sidebar"}
              title={pinned ? "Colapsar sidebar" : "Fijar sidebar"}
            >
              {pinned ? (
                <PanelLeftClose className="size-4" />
              ) : (
                <PanelLeftOpen className="size-4" />
              )}
            </button>
          )}
          {/* Botón cerrar en mobile */}
          {isMobile && (
            <button
              onClick={onMobileClose}
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground"
              aria-label="Cerrar menú"
            >
              <X className="size-5" />
            </button>
          )}
        </div>

        {/* Tenant chip - only visible when expanded */}
        <div
          className={cn(
            "overflow-hidden px-2 transition-all",
            expanded ? "max-h-20 pb-3 opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="flex items-center gap-2.5 rounded-lg border border-border bg-surface-2 px-3 py-2">
            <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-accent-orange to-accent-blue text-[10px] font-bold text-white">
              T1
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-foreground">
                ARVID Colombia
              </p>
              <p className="truncate text-[11px] text-muted-foreground">
                Plan Pro · T001
              </p>
            </div>
          </div>
        </div>

        {/* Just a spacer when collapsed */}
        {!expanded && <div className="h-2" />}

        {/* Nav */}
        <nav className="flex-1 px-2">
          <ul className="space-y-0.5">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavItem
                  item={item}
                  expanded={expanded}
                  onClick={handleNavClick}
                />
              </li>
            ))}
          </ul>
        </nav>

        {/* Allison status */}
        <div className="px-2 pb-3">
          <AllisonStatusCard expanded={expanded} />
        </div>

        {/* Footer */}
        <div className="border-t border-border px-2 py-2">
          <NavLink
            to="/settings"
            onClick={handleNavClick}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors",
                expanded ? "" : "justify-center",
                // Touch target mínimo en mobile
                isMobile && "min-h-11",
                isActive
                  ? "bg-surface-2 text-foreground"
                  : "text-muted-foreground hover:bg-surface-2 hover:text-foreground"
              )
            }
            title="Configuración"
          >
            <Settings className="size-4 shrink-0" />
            <span
              className={cn(
                "transition-opacity",
                expanded ? "opacity-100" : "hidden opacity-0"
              )}
            >
              Configuración
            </span>
          </NavLink>
        </div>
      </aside>
    </>
  );
}

/* -------------------------------------------------------------------------- */

interface NavItemProps {
  item: NavItem;
  expanded: boolean;
  onClick?: () => void;
}

function NavItem({ item, expanded, onClick }: NavItemProps) {
  const isMobile = useIsMobile();

  const content = (
    <>
      <item.icon className="size-4 shrink-0" />
      <span
        className={cn(
          "flex-1 truncate transition-opacity",
          expanded ? "opacity-100" : "hidden opacity-0"
        )}
      >
        {item.label}
      </span>
      {item.disabled && expanded && (
        <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          Pronto
        </span>
      )}
      {item.count !== undefined && item.count > 0 && expanded && (
        <span className="rounded-md bg-surface-3 px-1.5 py-0.5 text-[11px] font-medium tabular-nums">
          {item.count}
        </span>
      )}
      {item.count !== undefined && item.count > 0 && !expanded && (
        <span className="absolute right-1 top-1 size-1.5 rounded-full bg-brand" />
      )}
    </>
  );

  if (item.disabled) {
    return (
      <div
        className={cn(
          "relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-muted-foreground/60",
          !expanded && "justify-center",
          // Touch target en mobile
          isMobile && "min-h-11"
        )}
        title={item.label + " (Pronto)"}
      >
        {content}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors",
          !expanded && "justify-center",
          // Touch target en mobile
          isMobile && "min-h-11",
          isActive
            ? "bg-surface-2 text-foreground"
            : "text-muted-foreground hover:bg-surface-2 hover:text-foreground"
        )
      }
      title={item.label}
    >
      {content}
    </NavLink>
  );
}

function AllisonStatusCard({ expanded }: { expanded: boolean }) {
  if (!expanded) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-brand/20 bg-brand/[0.04] py-2"
        title="Allison · Atendiendo 24/7 · Latencia ~8s"
      >
        <AllisonOrb size={20} active />
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-lg border border-brand/20 bg-gradient-to-br from-brand/[0.08] to-transparent p-3">
      <div className="flex items-center gap-2.5">
        <AllisonOrb size={28} active />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground">Allison</p>
          <p className="truncate text-[11px] text-muted-foreground">
            Atendiendo 24/7
          </p>
        </div>
      </div>
      <div className="mt-2.5 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Latencia</span>
        <span className="font-mono tabular-nums text-foreground">~8s</span>
      </div>
    </div>
  );
}
